// Workspaces — which left-hand tabs a user sees (2026-09-29). The sidebar
// had grown to seven+ tabs serving three different jobs, so each team gets
// its own view of the app:
//
//   strategy  — high-level strategy/monitoring: network dashboard, the
//               long-term replacement strategy, project progress
//   packaging — the original package-creation pipeline: firm up packages
//               against the strategy team's targets, commit + lock projects
//   delivery  — project management of locked projects, phase by phase
//   construction / operations — (2026-10-02) field crews on a phone: the
//               "Field" tab opens field/fieldApp.ts full-screen
//   admin     — (2026-10-01) not a team: every admin+ user gets it. Its tabs
//               are the sections of the old single Admin tab (users & teams,
//               audit log, data access, data maintenance, diagnostics);
//               admin.ts hides the platform_admin-only ones from org admins.
//
// A user's workspaces come from their teams (backend auth/models.py:TEAMS,
// set on Admin > Users & teams — a user can be on several). Anyone with more
// than one workspace (admins: all of them, plus Admin) switches from the
// dropdown at the top of the sidebar (remembered per browser; the Admin
// workspace also remembers its last tab). Role gating still applies on top:
// a tab in the workspace stays hidden if the role check its own module does
// (admin.ts, dashboard.ts, packageSettingsPanel.ts) hides it — this module
// only adds the `ws-hidden` class, never un-hides.
import { isGisOnly } from "./dataProfile";
import { getState, hasProcurementAccess, roleAtLeast, setPackageMultiSelectState, setState } from "./state";
import type { CurrentUser, Team } from "./state";

// The brief import and the strategy year overlay live in the lazily loaded
// half of the app (fullApp.ts, 2026-10-05): importing them here would pull
// the map, deck.gl and the panels into the field app's boot bundle. fullApp
// registers them when it loads; until then there is nothing to cancel.
interface WorkspaceResetHooks {
  cancelBriefImport: () => void;
  isBriefImportActive: () => boolean;
  clearStrategyOverlay: () => void;
}
let resetHooks: WorkspaceResetHooks | null = null;

export function setWorkspaceResetHooks(hooks: WorkspaceResetHooks): void {
  resetHooks = hooks;
}

interface Workspace {
  label: string;
  tabs: string[]; // data-tab values, in addition to ALWAYS_TABS
  landing: string; // tab selected when switching into this workspace
}

// "approvals" (2026-10-08, approvalsInbox.ts): the managers' single inbox of
// decisions waiting on them - shown to manager+ only (the tab hides itself
// for everyone else), in every non-field workspace.
//
// "myWork" (2026-10-08, myWork.ts): every workspace's own "My work"
// dashboard - headline stats + open issues per category for this person
// (reports/role_dashboards/design.md). It is the landing tab where the job is
// a queue of things to act on; package creation keeps the map, and the field
// workspaces keep the full-screen field app.
export const WORKSPACES: Record<Team, Workspace> = {
  strategy: { label: "Strategy & monitoring", tabs: ["myWork", "approvals", "view", "dashboard", "strategy", "execCashflow", "briefs", "projects", "simulations"], landing: "myWork" },
  packaging: { label: "Package creation", tabs: ["view", "myWork", "approvals", "packageSettings", "wssViewer", "briefs", "projects", "simulations", "config"], landing: "view" },
  delivery: { label: "Project delivery", tabs: ["myWork", "approvals", "view", "myProjects", "deliveryBoard", "projects", "simulations"], landing: "myWork" },
  // Owns the asset register data: triages "there's a problem with this"
  // reports (assetIssues.ts), replies, records corrections.
  asset_data: { label: "Asset data", tabs: ["myWork", "approvals", "view", "assetIssues", "fieldReview"], landing: "myWork" },
  // Field workspaces (2026-10-02, field/fieldApp.ts): a full-screen,
  // mobile-first app opened from the "Field" tab - capital works crews
  // (projects, site map, progress / as-built / issue photos) and reactive
  // crews (near me, live jobs, fault triage, asset fault history).
  construction: { label: "Construction", tabs: ["field", "myWork", "myProjects", "projects"], landing: "field" },
  operations: { label: "Operations", tabs: ["field", "myWork", "view"], landing: "field" },
  // Flow plan stage D (executive.ts): read-only flow, bottleneck, budget and
  // outcome analytics. Open to the executive team and to manager+ by role
  // (the backend gate is require_role_or_team("manager", "executive")).
  executive: {
    label: "Executive",
    tabs: ["myWork", "approvals", "execOverview", "execBottlenecks", "execBudget", "execCashflow", "execOutcomes", "execProjects", "execImport"],
    landing: "myWork",
  },
  // Procurement (procurement.ts, 2026-10-02): tender rate sets from the
  // costing app, contractor comparison, rates library, package cost methods
  // side by side, published estimating bases. Tender rates are commercially
  // sensitive: the procurement team and manager+ only (backend
  // require_role_or_team("manager", "procurement")).
  procurement: {
    label: "Procurement",
    tabs: ["myWork", "approvals", "procTenders", "procCompare", "procRates", "procMethods", "procBasis", "procSuppliers", "tenderMap", "projects"],
    landing: "myWork",
  },
};

/** The Admin workspace's tabs (data-tab values), in sidebar order. Which of
 * them a user actually sees is admin.ts's call (role + data profile). */
export const ADMIN_TABS = ["myWork", "approvals", "adminUsers", "adminAudit", "adminAccess", "adminMaintenance", "adminDiagnostics", "adminContacts", "adminFeedback", "execImport"];

const ADMIN_WORKSPACE: Workspace = { label: "Admin", tabs: ADMIN_TABS, landing: "myWork" };

/** GIS-only data profile (dataProfile.ts, P7a): the one fixed map view. */
const GIS_WORKSPACE: Workspace = { label: "GIS view", tabs: ["view"], landing: "view" };

/** A team's workspace, the Admin workspace, or the GIS-only view. */
export type WorkspaceKey = Team | "admin" | "gis";

function workspaceDef(key: WorkspaceKey): Workspace {
  // A GIS-only organisation has no packages / projects to decide on, and
  // /api/approvals needs a full data profile: no Approvals tab for its admins.
  if (key === "admin") return isGisOnly() ? { ...ADMIN_WORKSPACE, tabs: ADMIN_TABS.filter((t) => t !== "approvals") } : ADMIN_WORKSPACE;
  if (key === "gis") return GIS_WORKSPACE;
  return WORKSPACES[key];
}

// Role-gated account tabs, shown in every workspace (their own modules decide
// whether the user may see them at all): the manager's Team tab. Admins
// manage users in the Admin workspace instead.
const ALWAYS_TABS = ["team"];

const STORAGE_KEY = "wcc.workspace";
const ADMIN_TAB_STORAGE_KEY = "wcc.adminTab";

let current: WorkspaceKey = "packaging";
let appliedOnce = false;

/** True when switching away would throw away unsaved work. */
function hasUnsavedWork(): boolean {
  const s = getState();
  return !!s.editingPackage || !!s.areaPackageWizard || !!resetHooks?.isBriefImportActive();
}

/** Clears everything map-/selection-related that belongs to the workspace
 * being left — highlighted/selected pipes and packages, multi-select, the
 * long-term-strategy year overlay, traffic-management colours, candidate or
 * import-preview pipes, an open project view, and any in-progress package
 * edit / area wizard / brief import (the caller confirms those first) — so
 * nothing highlighted in one workspace is dragged into another. */
/** The right-hand pane's "Clear": back to the workspace's default view.
 * Same reset as a workspace switch (closes a project view, drops strategy /
 * traffic overlays, selections and multi-select), minus the confirm, since
 * Clear shows "Discard" instead while a package edit is in progress. */
export function resetWorkspaceView(): void {
  resetCrossWorkspaceState();
}

function resetCrossWorkspaceState(): void {
  resetHooks?.clearStrategyOverlay();
  resetHooks?.cancelBriefImport();
  setPackageMultiSelectState({ multiSelectedPackages: [], pendingMapPackageMultiSelect: null });
  setState({
    selectedAssetId: null,
    highlightedPackageId: null,
    highlightedPackageAssetIds: null,
    highlightedAssetId: null,
    trafficManagementOverlay: null,
    nearbyCandidatePipes: null,
    editingPackage: null,
    areaPackageWizard: null,
    viewingProject: null,
    simulationOverlay: null,
  });
}

const TEAM_ORDER = Object.keys(WORKSPACES) as Team[];

/** The workspaces a user may use: every one for an admin, otherwise their
 * teams (several teams = several workspaces, switched from the dropdown);
 * no teams = one default (strategy for a manager, whose home the dashboard
 * used to be; the original package-creation view for everyone else). */
export function allowedWorkspaces(user: CurrentUser | null): Team[] {
  const all = allowedWorkspacesBeforeProcurementGate(user);
  if (hasProcurementAccess(user)) return all;
  // tender rates restricted to named users (backend procurement_access.py,
  // mirrored by routers/my_work.py allowed_workspaces): no Procurement workspace
  const rest = all.filter((t) => t !== "procurement");
  return rest.length ? rest : ["packaging"];
}

function allowedWorkspacesBeforeProcurementGate(user: CurrentUser | null): Team[] {
  if (roleAtLeast(user, "admin")) return TEAM_ORDER;
  const teams = TEAM_ORDER.filter((t) => user?.teams?.includes(t));
  // Flow plan stage D: any manager also gets the Executive workspace
  // (and, 2026-10-02, the Procurement one: the backend admits manager+)
  if (roleAtLeast(user, "manager")) {
    const base = teams.length ? teams : (["strategy"] as Team[]);
    const extra = (["executive", "procurement"] as Team[]).filter((t) => !base.includes(t));
    return [...base, ...extra];
  }
  if (teams.length) return teams;
  return [roleAtLeast(user, "manager") ? "strategy" : "packaging"];
}

/** Every workspace in the dropdown: the team workspaces (or, for a GIS-only
 * data profile, the single GIS view) plus Admin for admin+ users. */
export function workspaceChoices(user: CurrentUser | null): WorkspaceKey[] {
  const admin: WorkspaceKey[] = roleAtLeast(user, "admin") ? ["admin"] : [];
  // A GIS-view organisation keeps the field workspaces its users are on
  // (field capture is client-private; /api/field/nearby serves it the public
  // variant) next to the one fixed GIS view.
  if (isGisOnly()) {
    const field = (["construction", "operations"] as Team[]).filter((t) => user?.teams?.includes(t));
    return ["gis", ...field, ...admin];
  }
  return [...allowedWorkspaces(user), ...admin];
}

/** Where a user lands: package creation if they have it, else their first. */
export function defaultWorkspace(user: CurrentUser | null): Team {
  const own = TEAM_ORDER.filter((t) => user?.teams?.includes(t));
  const pool = own.length ? own : allowedWorkspaces(user);
  return pool.includes("packaging") ? "packaging" : pool[0];
}

export function currentWorkspace(): WorkspaceKey {
  return current;
}

function readKey(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage blocked — the choice just won't survive a reload
  }
}

/** The workspace this browser last used (not checked against the user). */
export function storedWorkspace(): string | null {
  return readKey(STORAGE_KEY);
}

/** The stored workspace, if it is still one of `choices`. */
function readStored(choices: WorkspaceKey[]): WorkspaceKey | null {
  const v = readKey(STORAGE_KEY);
  return v && (choices as string[]).includes(v) ? (v as WorkspaceKey) : null;
}

function selectTab(tab: string): void {
  document.querySelector<HTMLButtonElement>(`#sidebar .tab-btn[data-tab="${tab}"]`)?.click();
}

function isShown(btn: HTMLElement): boolean {
  return !btn.classList.contains("ws-hidden") && btn.style.display !== "none";
}

/** The tab to land on: the Admin workspace returns to its last-used tab
 * (if the user may still see it), every other workspace to its landing. */
function landingTab(key: WorkspaceKey, buttons: HTMLButtonElement[]): HTMLButtonElement | undefined {
  const def = workspaceDef(key);
  if (key === "admin") {
    const stored = readKey(ADMIN_TAB_STORAGE_KEY) ?? "";
    const last = def.tabs.includes(stored) ? buttons.find((b) => b.dataset.tab === stored) : undefined;
    if (last && isShown(last)) return last;
    return def.tabs.map((t) => buttons.find((b) => b.dataset.tab === t)).find((b) => !!b && isShown(b));
  }
  return buttons.find((b) => b.dataset.tab === def.landing);
}

/** Applies a workspace's tab set. `land` selects the workspace's landing tab
 * (on an explicit switch); otherwise only moves off the active tab if it
 * just became hidden. */
export function applyWorkspace(key: WorkspaceKey, land = false): void {
  current = key;
  appliedOnce = true;
  const order = [...workspaceDef(key).tabs, ...ALWAYS_TABS];
  const allowed = new Set(order);
  const buttons = [...document.querySelectorAll<HTMLButtonElement>("#sidebar .tab-btn[data-tab]")];
  for (const btn of buttons) {
    btn.classList.toggle("ws-hidden", !allowed.has(btn.dataset.tab ?? ""));
    // Tabs appear in the workspace's own order (flex `order`), not the HTML's.
    btn.style.order = String(Math.max(0, order.indexOf(btn.dataset.tab ?? "")));
  }
  document.body.dataset.workspace = key;
  // projectsUI.ts re-renders its summary/full mode on this.
  document.dispatchEvent(new CustomEvent("workspace-changed", { detail: { workspace: key } }));

  const landing = landingTab(key, buttons);
  const active = buttons.find((b) => b.classList.contains("active"));
  if (land && landing && isShown(landing)) {
    landing.click();
  } else if (!active || !isShown(active)) {
    const first = buttons.find(isShown);
    if (first) selectTab(first.dataset.tab!);
  }
}

/** Called once from main.ts after the role-gated tabs have been set up
 * (admin.ts's initAdmin decides which Admin tabs exist for this user).
 * The dropdown appears whenever the user may use more than one workspace.
 *
 * GIS-only data profile (dataProfile.ts, P7a): one fixed "GIS view" — the
 * View (map) tab plus the role-gated account tabs — and, for an admin, the
 * Admin workspace (their own organisation's users and audit log). Every
 * other tab stays hidden whatever the user's teams. */
export function initWorkspaces(preferred: WorkspaceKey | null = null): void {
  const user = getState().currentUser;
  const choices = workspaceChoices(user);
  const switcher = document.getElementById("workspace-switcher");
  const select = document.getElementById("workspace-select") as HTMLSelectElement | null;
  const fieldOnlyTeams = (user?.teams ?? []).filter((t) => t === "construction" || t === "operations");
  const fallback: WorkspaceKey = isGisOnly()
    ? (fieldOnlyTeams.length && fieldOnlyTeams.length === (user?.teams ?? []).length ? fieldOnlyTeams[0] : "gis")
    : defaultWorkspace(user);
  // `preferred`: main.ts's field boot (#field link, phone) picks the start
  const start = (preferred && choices.includes(preferred) ? preferred : null) ?? readStored(choices) ?? fallback;

  // Remember the last Admin tab the user opened (restored by landingTab).
  for (const tab of ADMIN_TABS) {
    document
      .querySelector<HTMLButtonElement>(`#sidebar .tab-btn[data-tab="${tab}"]`)
      ?.addEventListener("click", () => writeKey(ADMIN_TAB_STORAGE_KEY, tab));
  }

  if (choices.length < 2 || !switcher || !select) {
    if (switcher) switcher.hidden = true;
    applyWorkspace(start, true);
    return;
  }

  const isAdmin = roleAtLeast(user, "admin");
  select.replaceChildren();
  for (const key of choices) {
    const opt = document.createElement("option");
    opt.value = key;
    const label = workspaceDef(key).label;
    const mine = isAdmin && key !== "admin" && key !== "gis" && !!user?.teams?.includes(key);
    opt.textContent = mine ? `${label} (your team)` : label;
    select.append(opt);
  }
  select.value = start;
  select.addEventListener("change", () => {
    const key = select.value as WorkspaceKey;
    if (appliedOnce && key !== current) {
      if (hasUnsavedWork() && !confirm("Switching workspace discards the package edit / import in progress. Continue?")) {
        select.value = current;
        return;
      }
      resetCrossWorkspaceState();
    }
    writeKey(STORAGE_KEY, key);
    applyWorkspace(key, true);
  });
  switcher.hidden = false;
  applyWorkspace(start, true);
}

/** The label of a workspace key ("Project delivery", "Admin", ...). */
export function workspaceLabel(key: WorkspaceKey): string {
  return workspaceDef(key).label;
}

/** Switch to another of the user's workspaces as if picked from the
 * dropdown (same unsaved-work confirm). False when it isn't one of theirs
 * or the user cancelled. */
export function switchWorkspace(key: WorkspaceKey): boolean {
  if (key === current) return true;
  const select = document.getElementById("workspace-select") as HTMLSelectElement | null;
  if (!select || ![...select.options].some((o) => o.value === key)) return false;
  select.value = key;
  select.dispatchEvent(new Event("change"));
  return current === key;
}

export function teamLabels(teams: string[] | null | undefined): string {
  const labels = TEAM_ORDER.filter((t) => teams?.includes(t)).map((t) => WORKSPACES[t].label);
  return labels.length ? labels.join(", ") : "—";
}

/** A multi-team picker for the user-management screens (admin.ts,
 * managerUsers.ts): one checkbox per workspace. None ticked = the default
 * view. `onChange` fires with the new selection after each click. */
export function teamPicker(value: string[] | null, onChange?: (teams: Team[]) => void): { el: HTMLElement; value: () => Team[] } {
  const wrap = document.createElement("span");
  wrap.className = "team-picker";
  wrap.title = "Teams — which workspaces (sets of tabs) the user can use";
  const boxes: [Team, HTMLInputElement][] = [];
  for (const key of TEAM_ORDER) {
    const label = document.createElement("label");
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = !!value?.includes(key);
    boxes.push([key, cb]);
    label.append(cb, document.createTextNode(` ${WORKSPACES[key].label.split(" ")[0]}`));
    label.title = WORKSPACES[key].label;
    wrap.append(label);
  }
  const read = () => boxes.filter(([, cb]) => cb.checked).map(([k]) => k);
  if (onChange) for (const [, cb] of boxes) cb.addEventListener("change", () => onChange(read()));
  return { el: wrap, value: read };
}

/** Disables/enables every checkbox in a teamPicker's element. */
export function setTeamPickerDisabled(el: HTMLElement, disabled: boolean): void {
  el.querySelectorAll("input").forEach((i) => ((i as HTMLInputElement).disabled = disabled));
}
